// Payment panel for the Fashion layout. Renders the SuperMarket payment bar
// unchanged so tender, PAN, exchange, wallet, gift card, bank transfer, credit
// sale, round-off and "Complete Order" behave exactly as on layout 4. Only the
// frame around it is Fashion's.
import React, { useState } from "react";
import SMPaymentBar from "../Retail/SuperMarket/SMPaymentBar";
import useSuperMarketHandlers from "../Retail/SuperMarket/useSuperMarketHandlers";
import { showConfirm as showConfirmHelper, renderPaymentsData as renderPaymentsDataHelper } from "../Retail/SuperMarket/SMRenderHelpers";
import { showNextPaymentPage, showFirstPaymentPage, cyclePaymentMethod, modulo } from "../Retail/SuperMarket/paymentPaging";
import usePOSConfigStore from "../../../stores/usePOSConfigStore";
import useTransactionStore from "../../../stores/useTransactionStore";
import { RECEIPT_CHOICES, readReceiptChoice, receiptChannelsFor, usableReceiptChoice, writeReceiptChoice } from "../../../utils/fashion/receiptChoice";
import { billSavings, billTotals, gstLabel, gstSplit, isActiveLine, taxIncluded } from "../../../utils/fashion/billTotals";
import "./payV4.css";

function parseThemeJSON(posConfig) {
  const raw = posConfig?.themeJSON;
  if (!raw || raw === "N") return {};
  try {
    return (typeof raw === "string" ? JSON.parse(raw) : raw)?.layout4 || {};
  } catch {
    return {};
  }
}

// Board fx-pay wording for the shared summary card.
const SUMMARY_LABELS = { tendered: "Bill total", change: "Change to give", overpayment: "Rounded off", remaining: "Still to pay" };

// Quick cash amounts: the exact due, then the next round notes above it (board fx-pay: Exact, 2,800, 3,000, 3,500, 5,000).
export function quickCashAmounts(due) {
  const exact = Math.round(due * 100) / 100;
  if (!(exact > 0)) return [];
  const ups = [];
  [100, 500, 1000, 5000].forEach((step) => {
    const v = Math.ceil(exact / step) * step;
    if (v > exact && !ups.includes(v)) ups.push(v);
  });
  return [exact, ...ups.slice(0, 4)];
}

const FashionPaymentPanel = (props) => {
  const {
    cart,
    setCart,
    tillData,
    amount,
    setAmount,
    overPayedAmount,
    getFilteredPaymentMethods,
    startIndex,
    setStartIndex,
    setShowPayments,
    setSelectedPaymentMethod,
    setShowPaymentMethods,
    giftCardData,
    setGiftCardData,
    handleLoyaltyPayment,
    selectedCustomer,
    setSelectedCustomer,
    selectedPaymentMethod,
    onChangeAmount,
  } = props;

  const posConfig = usePOSConfigStore((s) => s.posConfig);
  const [receipt, setReceipt] = useState(() => readReceiptChoice(tillData));
  const chooseReceipt = (value) => {
    setReceipt(value);
    writeReceiptChoice(tillData, value);
  };
  // WhatsApp / Email only for a customer who can get it; otherwise this bill prints.
  const channels = receiptChannelsFor(cart?.customer);
  // This bill only: the till keeps the cashier's remembered choice for the next customer.
  const usableReceipt = usableReceiptChoice(receipt, cart?.customer);
  const currenciesList = tillData?.tillAccess?.csBunit?.currencies || [];
  const pricePrecision = currenciesList[0]?.prcPrecision ?? 2;
  const CWHardwareController = tillData?.tillAccess?.cwrTill?.printTemplate?.cwController === "Y";
  const selectedReturnType = useTransactionStore((s) => s.selectedReturnType);
  const isExchange = selectedReturnType === "EXCHANGE";
  const themeJSON = parseThemeJSON(posConfig);
  const [, setShowInputFields] = React.useState(false);

  // Note: the SuperMarket handler deletes a payment from the cart it was given
  // and writes it back, so two quick deletes can race on a stale cart. That is
  // shared layout-4 code and is left unchanged here.
  // Payment removal must reverse gift card, credit, loyalty and wallet
  // transactions and respect the exchange cart. Core's handleDeletePayment does
  // not, so use the SuperMarket handler (only its deletion is used here).
  const { handleDeletePayment } = useSuperMarketHandlers({
    cart,
    setCart,
    tillData,
    posConfig,
    pricePrecision,
    setGiftCardData,
    giftCardData,
    handleLoyaltyPayment,
    getFilteredPaymentMethods,
    setShowPayments,
    setAmount,
    isExchange,
    selectedCustomer,
    setSelectedCustomer,
  });

  const pagingCtx = { getFilteredPaymentMethods, cart, posConfig, tillData, startIndex, setShowPayments, setStartIndex, setSelectedPaymentMethod };
  const showMoreItems = () => showNextPaymentPage(pagingCtx);
  const showPreviousItems = () => showFirstPaymentPage(pagingCtx);
  const cyclePaymentMethodByKeyboard = (currentMethodId, direction) => cyclePaymentMethod(pagingCtx, currentMethodId, direction);

  const showConfirm = () => showConfirmHelper({ cart, amount, pricePrecision, posConfig, overPayedAmount });
  // Display only: group the paid amounts the way the till's locale reads them (12,450.00).
  const formatAmount = (value) =>
    (parseFloat(value) || 0).toLocaleString("en-IN", { minimumFractionDigits: pricePrecision, maximumFractionDigits: pricePrecision });
  // Feeds the summary's currency prefix in payV4.css; SMPaymentBar prints those amounts bare.
  const currencySymbol = currenciesList[0]?.symbolRightSide === "Y" ? "" : currenciesList[0]?.currSymbol || "";
  const isCash = selectedPaymentMethod?.name?.toLowerCase() === "cash";
  const due = Math.abs(cart?.total || 0) - (cart?.paid || 0);
  const pieces = (cart?.items || []).reduce((n, it) => n + Math.abs(parseFloat(it.weight ?? it.qty ?? 1) || 0), 0);
  const customerName = cart?.customer?.name && cart.customer.name !== "Walk-in" ? cart.customer.name : "Walk-in";
  // Same path as typing the amount into the field, so rounding and overpayment are worked out exactly as they are there.
  const quickCash = !isExchange && isCash && typeof onChangeAmount === "function" ? quickCashAmounts(due) : [];
  const cashShortcuts = quickCash.length > 0 && (
    <div className="fp-pay-quick" role="group" aria-label="Cash received">
      {quickCash.map((v, i) => (
        <button key={v} type="button" className={`fp-chip${parseFloat(amount) === v ? " is-on" : ""}`} aria-pressed={parseFloat(amount) === v} onClick={() => onChangeAmount({ target: { value: String(v) } })}>
          {i === 0 ? "Exact" : `${currencySymbol}${v.toLocaleString("en-IN")}`}
        </button>
      ))}
    </div>
  );
  const receiptToggle = (
    <div className="fp-pay-rc" role="radiogroup" aria-label="Receipt">
      <span className="fp-pay-by">Receipt</span>
      {[
        [RECEIPT_CHOICES.PRINT, "Print"],
        [RECEIPT_CHOICES.WHATSAPP, "WhatsApp"],
        [RECEIPT_CHOICES.EMAIL, "Email"],
        [RECEIPT_CHOICES.NONE, "No receipt"],
      ]
        .filter(([value]) => channels[value] !== false)
        .map(([value, label]) => (
          <button key={value} type="button" role="radio" aria-checked={usableReceipt === value} className={`fp-chip${usableReceipt === value ? " is-on" : ""}`} onClick={() => chooseReceipt(value)}>
            {label}
          </button>
        ))}
    </div>
  );
  // What the bill's tax is made of, under the steps: "Taxable ₹1,903.81 · GST 5% included ₹95.19
  // (CGST ₹47.60 + SGST ₹47.59)", and what the customer saved.
  const totals = billTotals(cart);
  const inclusive = taxIncluded(tillData);
  const money = (v) => `${currencySymbol}${formatAmount(v)}`;
  const saved = billSavings(totals);
  const taxLine = (totals.gst.length > 0 || saved > 0) && (
    <p className="fp-pay-tax fp-num" aria-label="Tax on this bill">
      {totals.gst.length > 0 && <span>Taxable {money(totals.taxable)}</span>}
      {totals.gst.map((g) => {
        const { cgst, sgst } = gstSplit(g.amount);
        return (
          <span key={g.rate}>
            {gstLabel(g.rate, inclusive)} {money(g.amount)}{" "}
            <span className="fp-muted">
              (CGST {money(cgst)} + SGST {money(sgst)})
            </span>
          </span>
        );
      })}
      {saved > 0 && <span className="fp-pay-saved">You saved {money(saved)}</span>}
    </p>
  );
  // The bill's lines, folded under one summary so the tenders stay in view.
  const billLines = (cart?.items || []).filter(isActiveLine);
  const linesTable = billLines.length > 0 && (
    <details className="fp-pay-lines">
      <summary>
        <span>{`${totals.units} ${totals.units === 1 ? "item" : "items"} on this bill`}</span>
        <span className="fp-num">{money(totals.toPay)}</span>
      </summary>
      <table>
        <thead>
          <tr>
            <th scope="col">Item</th>
            <th scope="col" className="fp-num">
              Qty
            </th>
            <th scope="col" className="fp-num">
              Amount
            </th>
          </tr>
        </thead>
        <tbody>
          {billLines.map((l, i) => (
            <tr key={l.lineId || `${l.productId}-${i}`}>
              <td>{l.name}</td>
              <td className="fp-num">{Math.abs(parseFloat(l.weight) || 0)}</td>
              <td className="fp-num">{money(l.nettotal)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </details>
  );
  const steps = (
    <ol className="fp-pay-steps" aria-label="Steps">
      <li className="is-done">Bag</li>
      <li className="is-on" aria-current="step">
        Payment
      </li>
      <li>Receipt</li>
    </ol>
  );
  const renderPaymentsData = () => renderPaymentsDataHelper({ cart, isExchange, currenciesList, pricePrecision, handleDeletePayment, formatAmount });

  return (
    <section className="fp-pay" aria-label="Payment" style={{ "--fp-cur": JSON.stringify(currencySymbol) }}>
      <div className="fp-pay-head">
        <button type="button" className="fp-btn-ghost fp-btn-sm" onClick={() => setShowPaymentMethods(false)}>
          Back to bill
        </button>
        <span className="fp-lbl">Payment</span>
        <span className="fp-muted fp-pay-who">
          {cart?.documentno && <span className="fp-num">Bill {cart.documentno} · </span>}
          {customerName} · {pieces} {pieces === 1 ? "item" : "items"}
        </span>
      </div>
      {steps}
      {linesTable}
      {taxLine}
      <div className="fp-pay-body">
        <span className="fp-pay-by">{(cart?.payments || []).length > 0 ? "Take the rest by" : "Take payment by"}</span>
        <SMPaymentBar
          {...props}
          posConfig={posConfig}
          currenciesList={currenciesList}
          setShowInputFields={setShowInputFields}
          showConfirm={showConfirm}
          isExchange={isExchange}
          exchangeCart={selectedReturnType}
          pricePrecision={pricePrecision}
          themeJSON={themeJSON}
          showMoreItems={showMoreItems}
          showPreviousItems={showPreviousItems}
          cyclePaymentMethodByKeyboard={cyclePaymentMethodByKeyboard}
          modulo={modulo}
          renderPaymentsData={renderPaymentsData}
          CWHardwareController={CWHardwareController}
          formatAmount={formatAmount}
          preselectMethod={props.tender || "cash"}
          summaryLabels={SUMMARY_LABELS}
          hideZeroSummaryRows
          paymentsHeading="Payments on this bill"
          afterAmountInput={cashShortcuts}
        />
        {receiptToggle}
      </div>
    </section>
  );
};

export default FashionPaymentPanel;
